import { useCallback, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { css } from '@linaria/core';
import { useToggler } from '@cbhq/cds-common';
import { Button } from '@cbhq/cds-web/buttons';
import { SelectOption } from '@cbhq/cds-web/controls';
import { Dropdown } from '@cbhq/cds-web/dropdown';
import { Icon } from '@cbhq/cds-web/icons';
import { Box, VStack } from '@cbhq/cds-web/layout';
import { NavigationTitle } from '@cbhq/cds-web/navigation/NavigationTitle';
import { ThemeProvider } from '@cbhq/cds-web/system';
import { TextHeadline } from '@cbhq/cds-web/typography';

import { useAppState } from ':cloud/contexts/AppStateContext';
import { useCreateProjectAndPushRoute } from ':cloud/hooks/useCreateProjectAndPushRoute';
import { useGetProject } from ':cloud/hooks/useGetProject';
import { useSimpleBreakpoints } from ':cloud/hooks/useSimpleBreakpoints';
import { useGetProjects } from ':cloud/queries/Projects/useGetProjects';
import { useGetUser } from ':cloud/queries/UserQueries/useGetUser';
import { AppRoute } from ':cloud/utils/routes';
import { ProjectSelectorOption } from ':cloud/widgets/projects/ProjectSelectorOption';
import { ProjectSettingsModal } from ':cloud/widgets/projects/ProjectSettingsModal';

const PROJECT_SELECTOR_CELL_HEIGHT = 58;
const PROJECT_SELECTOR_MIN_WIDTH = 310;
const PROJECT_SELECTOR_MIN_WIDTH_NARROW = 210;
const PROJECT_SELECTOR_LABEL_MAX_WIDTH = '28vw';

export function ProjectSelector() {
  const { isDesktop } = useSimpleBreakpoints();
  const [isSettingsModalVisible, { toggleOn: showSettingsModal, toggleOff: hideSettingsModal }] =
    useToggler(false);
  const [isDropdownOpen, { toggle: toggleDropdown }] = useToggler(false);
  const { selectedProject, setSelectedProject } = useAppState();
  const history = useHistory();
  const createProject = useCreateProjectAndPushRoute();
  const { activeOrg } = useGetUser();
  const { projects } = useGetProjects(activeOrg?.organizationId || '');
  const foundProject = useGetProject(selectedProject?.id || '');

  const handleSelectChange = useCallback(
    (newValue) => {
      if (newValue === 'create') {
        createProject();
        return;
      }
      if (newValue === selectedProject?.id) {
        showSettingsModal();
        return;
      }
      const newSelection = projects.find((project) => project.id === newValue);
      if (newSelection) {
        setSelectedProject(newSelection);
        history.push(`${AppRoute.Projects.Overview}?projectId=${newSelection.id}`);
      }
    },
    [createProject, history, projects, setSelectedProject, showSettingsModal, selectedProject],
  );

  const content = useMemo(
    () => (
      <VStack position="relative">
        {projects.map((project) => (
          <ProjectSelectorOption
            key={project.id}
            project={project}
            minHeight={PROJECT_SELECTOR_CELL_HEIGHT}
          />
        ))}
        {projects.length === 0 && (
          <SelectOption
            value="no-projects"
            title={
              <TextHeadline as="p" overflow="wrap" align="center">
                You don&apos;t have any projects yet. Create one below.
              </TextHeadline>
            }
            disabled
            compact
            minHeight={PROJECT_SELECTOR_CELL_HEIGHT * 2}
          />
        )}
        <VStack
          position="sticky"
          bottom={0}
          left={0}
          height={PROJECT_SELECTOR_CELL_HEIGHT}
          overflow="hidden"
        >
          <SelectOption
            value="create"
            key="create"
            accessory={
              <Box spacing={1} alignItems="center" justifyContent="center">
                <Icon size="xs" color="primary" name="add" />
              </Box>
            }
            title={
              <TextHeadline color="primary" as="p">
                Create new project
              </TextHeadline>
            }
            shouldOverflow={false}
            minHeight={PROJECT_SELECTOR_CELL_HEIGHT}
          />
        </VStack>
      </VStack>
    ),
    [projects],
  );

  useEffect(() => {
    // setting first project on global state if one isn't already set
    if (!selectedProject && projects.length) {
      setSelectedProject(projects[0]);
    }
  }, [projects, selectedProject, setSelectedProject]);

  useEffect(() => {
    if (foundProject) {
      setSelectedProject(foundProject);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- preventing re-render cycle
  }, [foundProject]);

  const truncateLabel = css`
    max-width: ${PROJECT_SELECTOR_LABEL_MAX_WIDTH};
  `;

  return (
    <>
      <Dropdown
        value={selectedProject?.id}
        onChange={handleSelectChange}
        content={content}
        minWidth={isDesktop ? PROJECT_SELECTOR_MIN_WIDTH : PROJECT_SELECTOR_MIN_WIDTH_NARROW}
        onCloseMenu={toggleDropdown}
        onOpenMenu={toggleDropdown}
      >
        <VStack justifyContent="center">
          <Button
            variant="secondary"
            transparent
            endIcon={isDropdownOpen ? 'caretUp' : 'caretDown'}
            flush="start"
            compact
            testID="project-selector-dropdown"
          >
            <NavigationTitle overflow="truncate" noWrap className={truncateLabel}>
              {selectedProject?.name || 'Create a project ...'}
            </NavigationTitle>
          </Button>
        </VStack>
      </Dropdown>
      <ThemeProvider scale="small">
        <ProjectSettingsModal visible={isSettingsModalVisible} onRequestClose={hideSettingsModal} />
      </ThemeProvider>
    </>
  );
}
